import { useRef, useState } from 'react';

import { Minus, Plus } from 'lucide-react';

import { useConfirmModal } from '@/hooks/useConfirmModal';
import { cn } from '@/lib/utils';

import InlineEdit from './InlineEdit';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const CustomTable = ({ mode = 'edit', onChange, value: defaultValue }) => {
  const columnLength = useRef(defaultValue?.[0].length || 3);
  const [fields, setFields] = useState(
    defaultValue || [
      ['', '', ''],
      ['', '', ''],
      ['', '', ''],
    ]
  );
  const { showConfirmModal } = useConfirmModal();
  const [headData, ...bodyData] = fields;

  const isEdit = mode !== 'static';

  const updateIntoValue = (nextValue) => {
    setFields(nextValue);
    onChange?.(nextValue);
  };

  const handleCellChange = (value, j, i = 0) => {
    const nextFields = [...fields];

    nextFields[i][j] = value;
    updateIntoValue(nextFields);
  };

  const handleAddRow = () => {
    updateIntoValue([...fields, new Array(columnLength.current).fill('')]);
  };

  const handleDeleteRow = (i) => {
    showConfirmModal({
      confirmText: 'OK',
      description: `Are you sure you want to delete this row?`,
      onConfirm: () =>
        updateIntoValue([...fields.slice(0, i + 1), ...fields.slice(i + 2)]),
      title: 'Delete Row',
    });
  };

  const handleAddColumn = () => {
    columnLength.current++;
    updateIntoValue(fields.map((row) => [...row, '']));
  };

  const handleDeleteColumn = (j) => {
    showConfirmModal({
      confirmText: 'OK',
      description: `Are you sure you want to delete this column?`,
      onConfirm: () => {
        columnLength.current--;
        updateIntoValue(
          fields.map((row) => [...row.slice(0, j), ...row.slice(j + 1)])
        );
      },
      title: 'Delete Column',
    });
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {headData.map((label, j) => (
                <TableHead
                  className={cn(`pr-0 relative group`)}
                  key={j}
                  style={{ width: `${parseInt((1 / headData.length) * 100)}%` }}
                >
                  <InlineEdit
                    disabled={!isEdit}
                    onChange={(value) => handleCellChange(value, j)}
                    placeholder="Column"
                    value={label}
                  />
                  {isEdit && (
                    <Button
                      className="absolute right-3 top-1/2 -translate-y-1/2 hidden group-hover:flex"
                      disabled={columnLength.current <= 1}
                      variant="outline"
                      onClick={() => handleDeleteColumn(j)}
                    >
                      <Minus />
                    </Button>
                  )}
                </TableHead>
              ))}
              {isEdit && (
                <TableHead>
                  <Button
                    variant="outline"
                    disabled={headData.length >= 10}
                    onClick={handleAddColumn}
                  >
                    <Plus />
                  </Button>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {bodyData.map((rowData, i) => (
              <TableRow className="group" key={i}>
                {rowData.map((label, j) => (
                  <TableCell key={j}>
                    <InlineEdit
                      disabled={!isEdit}
                      onChange={(value) => handleCellChange(value, j, i + 1)}
                      placeholder=""
                      value={label}
                    />
                  </TableCell>
                ))}
                {isEdit && (
                  <TableCell>
                    <Button
                      className="hidden group-hover:flex"
                      disabled={bodyData.length <= 1}
                      variant="outline"
                      onClick={() => handleDeleteRow(i)}
                    >
                      <Minus />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {isEdit && (
        <div className="mt-4">
          <Button variant="outline" onClick={handleAddRow}>
            <Plus />
          </Button>
        </div>
      )}
    </>
  );
};

export default CustomTable;
